// Locale-aware formatting.
//
// Every number, date and clock the student reads goes through here. Two rules
// this file exists to enforce:
//
//   1. NOTHING hardcodes Persian digits. The doctor client's `toFa()` habit
//      renders «۱۲» inside an otherwise English UI; `formatNumber` follows the
//      active locale instead.
//   2. Every date formatter pins `timeZone: 'Asia/Tehran'`. The instants on the
//      wire are UTC epochs and the student's day is an Iran day; formatting one
//      in the browser's zone dates a late-night study session to tomorrow for a
//      student abroad and to today at home — the worst kind of bug, because it
//      is right most of the time.
//
// Jalali comes from the locale tag (`-u-ca-persian`), never from an `if`: one
// code path renders both calendars, so they cannot drift.

// The ISO-day shape is imported rather than re-declared: lib/iranTime.js owns
// what "a calendar day on the wire" looks like, and this module has to agree
// with it about which strings get the noon-UTC anchor below.
import { ISO_DATE_RE } from '../lib/iranTime.js';

const INTL_TAG = {
  // arabext = ۰۱۲۳۴۵۶۷۸۹, the digits Persian users read.
  fa: 'fa-IR-u-ca-persian-nu-arabext',
  // English keeps the PERSIAN CALENDAR (core.md §7.9, platform rule): the
  // student's semester, exam and visit dates are Jalaali facts, and switching
  // the interface language changes the words — «تیر» becomes "Tir", the digits
  // become Latin — not the calendar the dates are counted in. en-GB rather than
  // en-US for the day-month order and the 24-hour clock, which match the
  // Persian side. Latin digits are the `en` default; `nu-latn` pins it so a
  // future tag edit cannot inherit the calendar's own numbering.
  en: 'en-GB-u-ca-persian-nu-latn',
};

const TZ = 'Asia/Tehran';

const DATE_STYLES = {
  // «شنبه، ۲۶ تیر» / «Sat, 17 Jul»
  weekdayDayMonth: { weekday: 'short', day: 'numeric', month: 'long' },
  // «شنبه، ۲۶ تیر ۱۴۰۴» / «Saturday, 17 July 2025»
  full: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
  // «۲۶ تیر» / «17 July»
  dayMonth: { day: 'numeric', month: 'long' },
  // «۱۴۰۴/۰۴/۲۶» / «17/07/2025»
  numeric: { day: '2-digit', month: '2-digit', year: 'numeric' },
};

/**
 * Accepts an epoch (number or the numeric strings the wire carries) or a plain
 * `YYYY-MM-DD`. A bare date is anchored at noon UTC so no timezone shift can
 * move it onto the neighbouring day.
 */
function toDate(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string' && ISO_DATE_RE.test(value)) {
    const ms = Date.parse(`${value}T12:00:00Z`);
    return Number.isNaN(ms) ? null : new Date(ms);
  }
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) return new Date(n);
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : new Date(parsed);
}

export function createFormatters(locale) {
  const tag = INTL_TAG[locale] || INTL_TAG.en;

  const number = new Intl.NumberFormat(tag);
  const time = new Intl.DateTimeFormat(tag, {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ,
  });
  // `formatToParts` minus the ERA: with a non-Gregorian calendar the English
  // formatter appends «AP» (Anno Persico) to every year — «25/03/1404 AP» — a
  // suffix no Iranian reader has seen on a date and the Persian side never
  // prints. Dropping the part, rather than a regex over the string, keeps the
  // rest of the locale's punctuation exactly as ICU shaped it.
  const withoutEra = (formatter) => ({
    format: (d) => formatter
      .formatToParts(d)
      .filter((part) => part.type !== 'era')
      .map((part) => part.value)
      .join('')
      .replace(/[\s,]+$/, ''),
  });
  const dates = Object.fromEntries(
    Object.entries(DATE_STYLES).map(([name, opts]) => [
      name, withoutEra(new Intl.DateTimeFormat(tag, { ...opts, timeZone: TZ })),
    ]),
  );

  /** A number in the active locale's digits and grouping. */
  const formatNumber = (n) => (
    Number.isFinite(Number(n)) ? number.format(Number(n)) : ''
  );

  /**
   * Localise the digits inside a string that is NOT a number — a clock («۰۹:۳۰»),
   * a file number, a national id. Grouping separators would corrupt those, which
   * is why this is a different function from formatNumber.
   */
  const formatDigits = (s) => {
    if (s == null) return '';
    const zero = locale === 'fa' ? 0x06F0 : 0x0030;
    return String(s).replace(/\d/g, (d) => String.fromCharCode(zero + Number(d)));
  };

  const formatDate = (value, style = 'weekdayDayMonth') => {
    const d = toDate(value);
    if (!d) return '';
    return (dates[style] || dates.weekdayDayMonth).format(d);
  };

  /** 24-hour, in Iran time, in both languages. A clinic runs on one clock. */
  const formatTime = (value) => {
    const d = toDate(value);
    return d ? time.format(d) : '';
  };

  return { formatNumber, formatDigits, formatDate, formatTime };
}
